import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { euro, formatApiErrorDetail } from "@/lib/format";
import { OperaioCard } from "@/components/cantieri/OperaioCard";
import { MovimentiDialog } from "@/components/cantieri/MovimentiDialog";
import { SpeseOperaiDialog } from "@/components/cantieri/SpeseOperaiDialog";
import { RiepilogoOperaioDialog, meseLabel, ultimiMesi } from "@/components/cantieri/RiepilogoOperaioDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BLANK = { nome: "", telefono: "", mansione: "", costo_orario: "", attivo: true, note: "" };

const OperaioDialog = ({ value, onClose, onSaved }) => {
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, costo_orario: Number(f.costo_orario) || 0 };
      if (f.id) await api.put(`/operai/${f.id}`, body); else await api.post("/operai", body);
      toast.success(f.id ? "Operaio aggiornato" : "Operaio aggiunto"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="operaio-dialog">
        <DialogHeader><DialogTitle className="font-heading">{f.id ? "Modifica operaio" : "Nuovo operaio"}</DialogTitle><DialogDescription className="sr-only">Dati operaio</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 col-span-2"><Label className="text-sm">Nome e cognome *</Label><Input value={f.nome} onChange={set("nome")} data-testid="operaio-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Telefono</Label><Input value={f.telefono || ""} onChange={set("telefono")} data-testid="operaio-phone-input" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Mansione</Label><Input value={f.mansione || ""} onChange={set("mansione")} placeholder="Muratore, manovale…" data-testid="operaio-role-input" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Costo orario (€)</Label><Input type="number" min="0" step="0.5" value={f.costo_orario} onChange={set("costo_orario")} data-testid="operaio-cost-input" /></div>
          <label className="flex items-center gap-2 text-sm pt-6"><Switch checked={f.attivo !== false} onCheckedChange={(v) => setF({ ...f, attivo: v })} data-testid="operaio-active-switch" /> Attivo</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={save} disabled={busy || !f.nome} data-testid="operaio-save-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OperaiTab = ({ operai, onChange, openTransfer = false }) => {
  const [edit, setEdit] = useState(null);
  const [toDel, setToDel] = useState(null);
  const [mov, setMov] = useState(null);
  const [sum, setSum] = useState({});
  const [sugg, setSugg] = useState([]);
  const [transfer, setTransfer] = useState(false);
  const [mese, setMese] = useState("");
  const [riep, setRiep] = useState(null);
  const refresh = useCallback(() => {
    api.get("/operai/riepilogo", { params: { mese: mese || undefined } }).then((r) => setSum(Object.fromEntries(r.data.map((x) => [x.operaio_id, x])))).catch(() => {});
    api.get("/operai/spese-suggerite").then((r) => setSugg(r.data)).catch(() => {});
  }, [mese]);
  useEffect(() => { refresh(); }, [refresh, operai]);
  useEffect(() => { if (openTransfer && sugg.length) setTransfer(true); }, [openTransfer, sugg.length]);

  const changed = () => { onChange(); refresh(); };
  const del = async () => { await api.delete(`/operai/${toDel.id}`); toast.success(`${toDel.nome} eliminato`); setToDel(null); changed(); };
  const setActive = async (o, attivo) => {
    try { await api.put(`/operai/${o.id}`, { ...o, attivo }); toast.success(attivo ? `${o.nome} attivo` : `${o.nome} non attivo`); changed(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const totDue = Object.values(sum).reduce((s, x) => s + Math.max(0, x.da_pagare_totale || 0), 0);
  const totAdv = Object.values(sum).reduce((s, x) => s + (x.anticipi || 0), 0);

  return (
    <div className="space-y-4" data-testid="operai-tab">
      {sugg.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 flex flex-wrap items-center gap-3" data-testid="operai-spese-banner">
          <ArrowRightLeft className="w-5 h-5 text-amber-600 shrink-0" />
          <span className="flex-1 min-w-[200px] text-sm">In <b>Spese di gestione</b> ci sono <b>{sugg.length}</b> {sugg.length === 1 ? "spesa che sembra" : "spese che sembrano"} di un operaio. Spostale sulla sua scheda così non vengono contate due volte.</span>
          <Button onClick={() => setTransfer(true)} data-testid="operai-spese-open">Controlla e sposta</Button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={mese || "tutto"} onValueChange={(v) => setMese(v === "tutto" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-52" data-testid="operai-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutto">Da sempre</SelectItem>
            {ultimiMesi().map((m) => <SelectItem key={m} value={m}>{meseLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground" data-testid="operai-totals">Anticipi {mese ? "del mese" : "dati"} <b className="text-foreground">{euro(totAdv)}</b> · Da pagare in tutto <b className="text-foreground">{euro(totDue)}</b></div>
        <Button className="ml-auto" onClick={() => setEdit({ ...BLANK })} data-testid="new-operaio-button"><Plus className="w-4 h-4 mr-1.5" /> Nuovo operaio</Button>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        {operai.map((o) => <OperaioCard key={o.id} o={o} s={sum[o.id]} onEdit={(x) => setEdit({ ...x })} onDelete={setToDel} onToggle={setActive} onMovimenti={setMov} onRiepilogo={setRiep} mese={mese} />)}
      </div>
      {operai.length === 0 && <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">Nessun operaio. Aggiungi la tua squadra per segnare le ore.</div>}
      {edit && <OperaioDialog value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); changed(); }} />}
      {riep && <RiepilogoOperaioDialog operaio={riep} mese={mese} s={sum[riep.id]} onClose={() => setRiep(null)} />}
      {mov && <MovimentiDialog operaio={mov} onClose={() => setMov(null)} onChanged={refresh} />}
      {transfer && <SpeseOperaiDialog items={sugg} onClose={() => setTransfer(false)} onDone={() => { setTransfer(false); refresh(); }} />}
      <AlertDialog open={!!toDel} onOpenChange={(o) => !o && setToDel(null)}>
        <AlertDialogContent data-testid="operaio-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {toDel?.nome}?</AlertDialogTitle>
            <AlertDialogDescription>L'operaio viene tolto anche dai cantieri. Se vuoi solo sospenderlo, usa l'interruttore «attivo».</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="operaio-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OperaiTab;
